import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import { History as HistoryIcon } from "@mui/icons-material";
import { RoleplayResponse } from "../../../redux/actions/roleplayActions";

interface RoleplayHistoryProps {
  currentRoleplayId: string;
  roleplayHistory: RoleplayResponse[];
  historyLoading: boolean;
}

const RoleplayHistory: React.FC<RoleplayHistoryProps> = ({
  currentRoleplayId,
  roleplayHistory,
  historyLoading,
}) => {
  const navigate = useNavigate();

  if (!roleplayHistory || roleplayHistory.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <HistoryIcon color="primary" />
        <Typography variant="h6">Roleplay History</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {historyLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Roleplay ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...roleplayHistory]
                .sort(
                  (a, b) =>
                    new Date(b.creation_time).getTime() -
                    new Date(a.creation_time).getTime()
                )
                .map((roleplay, index) => (
                  <TableRow
                    key={roleplay.id}
                    sx={{
                      bgcolor:
                        roleplay.id === currentRoleplayId
                          ? "action.selected"
                          : "inherit",
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          v{roleplayHistory.length - index}
                        </Typography>
                        {roleplay.id === currentRoleplayId && (
                          <Chip label="Current" size="small" color="primary" />
                        )}
                        {index === 0 && roleplay.is_active && (
                          <Chip label="Active" size="small" color="success" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {roleplay.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={roleplay.is_active ? "Active" : "Inactive"}
                        size="small"
                        color={roleplay.is_active ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={roleplay.environment}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(roleplay.creation_time).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {roleplay.id !== currentRoleplayId && (
                        <Button
                          size="small"
                          onClick={() =>
                            navigate(
                              `/dashboard/roleplays/${roleplay.organization_id}/${roleplay.id}`
                            )
                          }
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default RoleplayHistory;
